import { t } from 'i18next';
import Cookies from 'js-cookie';

import { CreateCompanyRequest } from '../../../../typings/api/skymap/rest/v0/company';
import { Company } from '../../react/components/register-company/state/form-state-provider';
import { ThemeManager } from '../../react/themes/theme-manager';
import { assertNever } from '../variables';

/**
 * Selects the specified language in the hidden Google Translation bar.
 * @param language Language to change to.
 */
export function changeLanguage(language: string) {
  const element = document.querySelector('.goog-te-combo') as HTMLSelectElement;

  // Do nothing if Google Translate dropdown is not found.
  if (!element) {
    return;
  }

  element.value = language;

  // Have to dispatch event twice. Otherwise translation is not triggered sometimes.
  element.dispatchEvent(new Event('change'));
  element.dispatchEvent(new Event('change'));
}

export function getDefaultLanguageByHostname() {
  return ThemeManager.instance.hostnameStartsWith('moba-de') ? 'de' : 'sv';
}

/**
 * Returns language code set in Google Translation API cookie, or 'sv' if no cookie exists.
 */
export function getLanguageFromCookie(): string {
  const cookieValue = Cookies.get('googtrans');
  return cookieValue
    ? cookieValue.substring(cookieValue.lastIndexOf('/') + 1)
    : getDefaultLanguageByHostname();
}

export function hasLanguageCookie(): boolean {
  return Cookies.get('googtrans') !== undefined;
}

/**
 * Returns language drop down list item representing the language set in Google Translation API cookie, or item for 'sv' if no cookie exists.
 */
export function getLanguageItemFromCookie() {
  const language = getLanguageFromCookie();
  return (
    languageDropdownListItems.find((x) => x.value === language) ??
    languageDropdownListItems.find((x) => x.value === getDefaultLanguageByHostname())!
  );
}

const supportedLanguages = ['de', 'en', 'es', 'fi', 'fr', 'no', 'sv'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const isValidLanguage = (lng: string): lng is SupportedLanguage =>
  supportedLanguages.includes(lng as SupportedLanguage);

export const mapCountryCodeToLanguageCode = (
  countryCode: NonNullable<CreateCompanyRequest['countryCode']>,
): SupportedLanguage => {
  switch (countryCode) {
    case 'DE':
      return 'de';
    case 'FI':
      return 'fi';
    case 'NO':
      return 'no';
    case 'SE':
      return 'sv';
  }
};

export const mapLanguageCodeToCountryCode = (
  countryCode: SupportedLanguage,
): NonNullable<CreateCompanyRequest['countryCode']> => {
  switch (countryCode) {
    case 'de':
      return 'DE';
    case 'fi':
      return 'FI';
    case 'no':
      return 'NO';
    case 'sv':
      return 'SE';
  }

  return 'SE';
};

/**
 * List of language drop down list items.
 */
export const languageDropdownListItems: { value: SupportedLanguage; text: string }[] = [
  { text: 'Deutsch', value: 'de' },
  { text: 'English', value: 'en' },
  { text: 'Español', value: 'es' },
  { text: 'Français', value: 'fr' },
  { text: 'Norsk', value: 'no' },
  { text: 'Suomi', value: 'fi' },
  { text: 'Svenska', value: 'sv' },
];

export const countryDropdownListItems: {
  value: Company['countryCode'];
  englishText: string;
  nativeText: string;
  flag: string;
}[] = [
  { value: 'DE', englishText: 'Germany', nativeText: 'Deutschland', flag: '🇩🇪' },
  { value: 'FI', englishText: 'Finland', nativeText: 'Suomi', flag: '🇫🇮' },
  { value: 'NO', englishText: 'Norway', nativeText: 'Norge', flag: '🇳🇴' },
  { value: 'SE', englishText: 'Sweden', nativeText: 'Sverige', flag: '🇸🇪' },
];

export const translateCountryByAlpha2Code = (code: Company['countryCode']) => {
  switch (code) {
    case 'DE':
      return t('country.germany', { ns: 'common' });
    case 'FI':
      return t('country.finland', { ns: 'common' });
    case 'NO':
      return t('country.norway', { ns: 'common' });
    case 'SE':
      return t('country.sweden', { ns: 'common' });
    default:
      assertNever(code);
  }
};
